import { ThemeColors } from '../services/api'

// Neutral fallback used before the tenant's theme loads. Avoids flashing
// any specific tenant's brand colors on first paint.
export const DEFAULT_THEME: ThemeColors = {
  primary: '#3b82f6',
  primaryHover: '#2563eb',
  secondary: '#1e293b',
  secondaryDark: '#0f172a',
  bgPrimary: '#0f172a',
  bgSecondary: '#1e293b',
  textPrimary: '#f8fafc',
  textSecondary: '#94a3b8',
}

// Convert hex color to RGB values string (e.g., "#FFD700" -> "255 215 0")
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return '0 0 0'
  return `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`
}

// WCAG relative luminance + contrast ratio for a #rrggbb colour.
function luminance(hex: string): number {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!m) return 0
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => {
    const c = parseInt(h, 16) / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function contrastRatio(a: string, b: string): number {
  const la = luminance(a)
  const lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/**
 * True when the theme is "light": the tenant's own text colour reads better
 * on its background than white does. The public site (and admin) were
 * designed dark-first with ~700 hard-coded `text-white` / `bg-white/5` /
 * `border-white/10` neutrals, which go white-on-white on a light palette
 * (#193). Rather than rewrite every class, Tailwind's `white` resolves to
 * `--color-white`, which is plain white for dark themes (pixel-identical)
 * and the theme's textPrimary for light ones, so every white neutral
 * becomes "the foreground colour". Surfaces that must stay white whatever
 * the theme (text over photos, team-colour banners, broadcast overlays)
 * opt out with the `.keep-white` class or the `fixed-white` colour.
 */
export function isLightTheme(theme: Pick<ThemeColors, 'bgPrimary' | 'textPrimary'>): boolean {
  return contrastRatio(theme.textPrimary, theme.bgPrimary) > contrastRatio('#ffffff', theme.bgPrimary)
}

/**
 * Plain-language warnings for colour pairs the public site actually draws
 * text on. Shown in the theme editor so a half-light palette (e.g. a light
 * Background but a dark Secondary Dark, which paints page gradients) is
 * flagged before it ships (#193). Thresholds: WCAG AA 4.5:1 for body text,
 * 3:1 for muted text and large page-header areas.
 */
export function themeContrastWarnings(theme: ThemeColors): string[] {
  const warnings: string[] = []
  const check = (fg: string, bg: string, min: number, msg: string) => {
    const ratio = contrastRatio(fg, bg)
    if (ratio < min) warnings.push(`${msg} (contrast ${ratio.toFixed(1)}:1, aim for ${min}:1)`)
  }
  check(theme.textPrimary, theme.bgPrimary, 4.5, 'Text Primary is hard to read on Background')
  check(theme.textPrimary, theme.bgSecondary, 4.5, 'Text Primary is hard to read on Background Secondary (cards, tables)')
  check(theme.textSecondary, theme.bgPrimary, 3, 'Text Secondary is hard to read on Background')
  check(theme.textPrimary, theme.secondaryDark, 3, 'Text Primary is hard to read on Secondary Dark (page header gradients)')
  return warnings
}

export function applyThemeToDOM(theme: ThemeColors) {
  const root = document.documentElement
  const light = isLightTheme(theme)
  root.style.setProperty('--color-white', light ? hexToRgb(theme.textPrimary) : '255 255 255')
  root.dataset.themeTone = light ? 'light' : 'dark'
  // Set RGB values for Tailwind opacity support
  root.style.setProperty('--color-primary', hexToRgb(theme.primary))
  root.style.setProperty('--color-primary-hover', hexToRgb(theme.primaryHover))
  root.style.setProperty('--color-secondary', hexToRgb(theme.secondary))
  root.style.setProperty('--color-secondary-dark', hexToRgb(theme.secondaryDark))
  root.style.setProperty('--color-bg-primary', hexToRgb(theme.bgPrimary))
  root.style.setProperty('--color-bg-secondary', hexToRgb(theme.bgSecondary))
  root.style.setProperty('--color-text-primary', hexToRgb(theme.textPrimary))
  root.style.setProperty('--color-text-secondary', hexToRgb(theme.textSecondary))
  // Also set hex versions for inline styles
  root.style.setProperty('--color-primary-hex', theme.primary)
  root.style.setProperty('--color-primary-hover-hex', theme.primaryHover)
  root.style.setProperty('--color-secondary-hex', theme.secondary)
  root.style.setProperty('--color-secondary-dark-hex', theme.secondaryDark)
  root.style.setProperty('--color-bg-primary-hex', theme.bgPrimary)
  root.style.setProperty('--color-bg-secondary-hex', theme.bgSecondary)
  root.style.setProperty('--color-text-primary-hex', theme.textPrimary)
  root.style.setProperty('--color-text-secondary-hex', theme.textSecondary)
}

export function applyFaviconToDOM(url: string) {
  const links = document.querySelectorAll("link[rel='icon']") as NodeListOf<HTMLLinkElement>
  links.forEach(link => { link.href = url })
}
